import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import renderMarkdown from '../PageBuilder/markdownProcessor';
import { Link as MarkdownLink } from '../PageBuilder/Primitives/Link';

import { HELP_HOME, HELP_TOPICS, findHelpTopic } from './helpContent';
import css from './HelpPage.module.css';

const HelpMarkdownLink = props => <MarkdownLink {...props} rootClassName={css.link} />;
const MARKDOWN_COMPONENTS = { a: HelpMarkdownLink };

const Markdown = ({ md }) => (
  <div className={css.markdown}>{renderMarkdown(md, MARKDOWN_COMPONENTS)}</div>
);

// Markdown links -> their text, for the plain-text answers in FAQPage data
export const markdownToPlainText = md =>
  md
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^\s*(?:[-*]|\d+\.)\s+/gm, '')
    .replace(/\n{2,}/g, '\n')
    .trim();

const HelpBlock = ({ block }) => {
  switch (block.type) {
    case 'qa':
      return (
        <section className={css.block}>
          <h2 className={css.blockTitle}>{block.q}</h2>
          <Markdown md={block.a} />
        </section>
      );
    case 'section':
      return (
        <section className={css.block} id={block.id}>
          <h2 className={css.blockTitle}>{block.title}</h2>
          <Markdown md={block.md} />
        </section>
      );
    case 'table':
      return (
        <div className={css.tableWrapper}>
          <table className={css.table}>
            <thead>
              <tr>
                {block.head.map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'md':
      return <Markdown md={block.md} />;
    default:
      return null;
  }
};

const HelpNav = ({ currentTopic }) => (
  <nav className={css.nav} aria-label="Help topics">
    <ul className={css.navList}>
      <li>
        <NamedLink
          name="HelpPage"
          className={classNames(css.navLink, { [css.navLinkActive]: !currentTopic })}
        >
          Help home
        </NamedLink>
      </li>
      {HELP_TOPICS.map(t => (
        <li key={t.topic}>
          <NamedLink
            name="HelpTopicPage"
            params={{ topic: t.topic }}
            className={classNames(css.navLink, {
              [css.navLinkActive]: currentTopic === t.topic,
            })}
          >
            {t.navLabel}
          </NamedLink>
        </li>
      ))}
    </ul>
  </nav>
);

export const HelpPageComponent = props => {
  const { params, scrollingDisabled, staticContext } = props;
  const config = useConfiguration();
  const rootURL = config?.marketplaceRootURL || '';
  const currentTopic = params?.topic || null;
  const page = currentTopic ? findHelpTopic(currentTopic) : HELP_HOME;

  if (!page) {
    return <NotFoundPage staticContext={staticContext} />;
  }

  const path = currentTopic ? `/help/${currentTopic}` : '/help';
  const faqItems = page.blocks.filter(b => b.type === 'qa');
  const breadcrumb = {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Help', item: `${rootURL}/help` },
      ...(currentTopic
        ? [{ '@type': 'ListItem', position: 2, name: page.title, item: `${rootURL}${path}` }]
        : []),
    ],
  };
  const faqPage =
    faqItems.length > 0
      ? {
          '@context': 'http://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqItems.map(b => ({
            '@type': 'Question',
            name: b.q,
            acceptedAnswer: { '@type': 'Answer', text: markdownToPlainText(b.a) },
          })),
        }
      : null;
  const webPage = {
    '@context': 'http://schema.org',
    '@type': 'WebPage',
    name: page.metaTitle,
    description: page.description,
    url: `${rootURL}${path}`,
  };

  return (
    <Page
      title={page.metaTitle}
      description={page.description}
      scrollingDisabled={scrollingDisabled}
      schema={[webPage, breadcrumb, ...(faqPage ? [faqPage] : [])]}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <HelpNav currentTopic={currentTopic} />
          <article className={css.content}>
            <h1 className={css.title}>{page.title}</h1>
            {page.intro ? <p className={css.intro}>{page.intro}</p> : null}
            {page.blocks.map((block, i) => (
              <HelpBlock key={i} block={block} />
            ))}
          </article>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const HelpPage = compose(
  withRouter,
  connect(mapStateToProps)
)(HelpPageComponent);

export default HelpPage;
